<!--
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
-->
<div id="top"></div>
<div class="help">
  <div class="heading">Help/Frequently Asked Questions (FAQ)</div>
  <div class="intro">
    Having trouble? Here are some frequently asked questions about downloading and installing Wind River products.
  </div>

  <div class="toc">
    <ol>
      <li>
        <a (click)="scrollTo('question1')">What are the system requirements for downloading and installing?</a>
      </li>
      <li>
        <a (click)="scrollTo('question2')">I am having issues downloading the software. What can be the problem?</a>
      </li>
      <li>
        <a (click)="scrollTo('question3')"
          >After installing and launching my application (ex. Workbench), it asks for a license. Where do I get the
          license file?</a
        >
      </li>
      <li>
        <a (click)="scrollTo('question4')">What is the approximate size of download I am to expect?</a>
      </li>
      <li>
        <a (click)="scrollTo('question5')">Can I have installation media sent to me?</a>
      </li>
      <li>
        <a (click)="scrollTo('question6')">Where can I get more information on how to use the product?</a>
      </li>
      <li>
        <a (click)="scrollTo('question7')">Where can I find Legal Notices?</a>
      </li>
      <li>
        <a (click)="scrollTo('question8')"
          >How do I perform an offline install of a Wind River Linux release, for example when installing in an isolated
          or air-gapped network?</a
        >
      </li>
    </ol>
  </div>

  <div class="question" id="question1">1. What are the system requirements for downloading and installing?</div>
  <div class="answer">
    <div class="section">
      Host system requirements can be found in the
      <a [href]="documentationUrl" target="_blank"
        >Wind River Product Installation and Licensing Administrator's Guide.</a
      >
    </div>
  </div>

  <div class="question" id="question2">2. I am having issues downloading the software. What can be the problem?</div>
  <div class="answer">
    <div class="section">
      For downloading from Wind River Gallery, we use HTTPS which requires Port 443 to be open and access to our
      gallery.windriver.com domain.
    </div>
    <div class="section">
      If your company restricts the download of ZIP archive files, that may also prevent download.
    </div>
    <div class="section">
      Your network configuration may prevent the delivery of Wind River software. This is most often caused by firewall
      or proxy server settings that block certain kinds of software installations. In addition, some networks are setup
      to throttle the bandwidth of large files preventing them from completing the download or block downloading of zip
      or exe file formats.
    </div>
    <div class="section">
      If you think you might have one of these issues, please contact your IT administrator to resolve the issue. Your
      IT Admin can usually resolve the issue by adjusting the proxy server settings. If your IT administrator cannot
      resolve the issue for you, <a [href]="supportUrl" target="_blank">contact Wind River Support</a>.
    </div>
  </div>

  <div class="question" id="question3">
    3. After installing and launching my application (ex. Workbench), it asks for a license. Where do I get the license
    file?
  </div>
  <div class="answer">
    <div class="section">
      Most Wind River products require a runtime license file to use them. You can generate a license file for your
      order from our <a [href]="licensingUrl" target="_blank">Licensing Portal</a>.
    </div>
  </div>

  <div class="question" id="question4">4. What is the approximate size of download I am to expect?</div>
  <div class="answer">
    <div class="section">
      Approximate sizes are shown in the table below. Sizes will vary depending on add-on purchases included and product
      updates. Online Installation file sizes represent the size of the initial Installer download. During the
      installation process, additional files are downloaded to reach sizes similar to Offline size.
    </div>
    <div class="section">
      Many Wind River software packages are very large (usually several GB, and sometimes as much as 88 GB); see the
      short list below for some examples:
    </div>
    <div class="section">
      <table>
        <tr>
          <th>Product</th>
          <th>
            Offline Installation File Size<br /><span class="subheading"
              >(Core product. Add-on profiles may add to the size)</span
            >
          </th>
          <th>Online Installation File Size<br /><span class="subheading">(Initial Download Only)</span></th>
        </tr>
        <tr>
          <td>VxWorks 7</td>
          <td>12 GB</td>
          <td>90 MB</td>
        </tr>
        <tr>
          <td>Wind River Linux 9</td>
          <td>N/A</td>
          <td>N/A (Git Cloning)</td>
        </tr>
        <tr>
          <td>Wind River Linux 7 and 8</td>
          <td>N/A</td>
          <td>90 MB</td>
        </tr>
        <tr>
          <td>Diab 5.9</td>
          <td>1 GB</td>
          <td>90 MB</td>
        </tr>
      </table>
    </div>
  </div>

  <div class="question" id="question5">5. Can I have installation media sent to me?</div>
  <div class="answer">
    <div class="section">
      Wind River Gallery products via electronic software download (ESD) except those products listed here:
      <a [href]="esdSupportUrl" target="_blank">{{ esdSupportUrl }}</a
      >.
    </div>
  </div>

  <div class="question" id="question6">6. Where can I get more information on how to use the product?</div>
  <div class="answer">
    <div class="section">
      You can find all product related guides and documents on our
      <a [href]="documentationUrl" target="_blank">Wind River Support Network (WRSN)</a>.
    </div>
  </div>

  <div id="legal_notices"></div>
  <div class="question" id="question7">7. Where can I find Legal Notices?</div>
  <div class="answer">
    <div class="section">
      Wind River provides the required third party legal notices and open source code when a product is downloaded. That
      is, the required IP compliance artifacts for a given product are provided at the same time and placed in your
      Artifacts file folders. To locate the compliance artifacts for products you have purchased, go to
      <a [href]="minioUrl" target="_blank">here</a> and look for the Legal Notices and Open Source Code Distribution
      folders for the desired product. Example folders for VxWorks 7 are VxWorks-Legal-Notices and
      VxWorks-Open-Source-Code-Distribution.
    </div>
  </div>

  <div class="question" id="question8">
    8. How do I perform an offline install of a Wind River Linux release, for example when installing in an isolated or
    air-gapped network?
  </div>
  <div class="answer">
    <div class="section">
      Offline installation option for Wind River Linux products has been removed in favour of the method documented in
      <a [href]="wrLinuxMirrorCreation" target="_blank">Creating a mirror for local Project builds</a>.
    </div>
  </div>
</div>
<div class="deadspace"></div>
